/**
 * PDP purchase options — scoped selection + variant/qty sync.
 * Mixed-variant packs (opt-in): slot state, readiness, dynamic checkout visibility.
 * TODO: Integrate Shopify Bundles / bundle app line items when bundle SKUs are configured.
 */
(function () {
  'use strict';

  function qs(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }

  function qsa(sel, ctx) {
    return Array.from((ctx || document).querySelectorAll(sel));
  }

  function getRoot(ctx) {
    if (ctx && ctx.matches && ctx.matches('[data-pdp-purchase-options]')) return ctx;
    if (ctx) {
      var nested = qs('[data-pdp-purchase-options]', ctx);
      if (nested) return nested;
    }
    return qs('[data-pdp-purchase-options]');
  }

  function getHeroForm(root) {
    var hero = root.closest('[data-pdp-hero], .pdp-hero') || qs('[data-pdp-hero], .pdp-hero');
    if (!hero) return null;
    return qs('#pdp-product-form', hero) || qs('form.product-form', hero) || qs('form[action*="/cart/add"]', hero);
  }

  function isMixedPackOption(option) {
    if (!option) return false;
    return option.dataset.selectionMode === 'mixed_variants' && option.dataset.mixedVariantsEnabled === 'true';
  }

  function isSingleVariantOption(option) {
    if (!option) return false;
    return option.dataset.singleVariantEnabled === 'true';
  }

  function hasVariantSlotPanel(option) {
    return isMixedPackOption(option) || isSingleVariantOption(option);
  }

  function getSelectedOption(root) {
    return qs('[data-purchase-option].is-selected', root) || qs('[data-purchase-option]', root);
  }

  function getVariants(root) {
    var script = qs('[data-pdp-product-variants]', root);
    if (!script) return [];
    try {
      return JSON.parse(script.textContent);
    } catch (err) {
      console.warn('[pdp-purchase-options] variant JSON parse failed', err);
      return [];
    }
  }

  function getVariantMap(root) {
    var map = Object.create(null);
    getVariants(root).forEach(function (variant) {
      map[String(variant.id)] = variant;
    });
    return map;
  }

  function getSlotPanel(root, optionIndex) {
    return qs('[data-purchase-option-slots][data-option-index="' + optionIndex + '"]', root);
  }

  function getOptionSlotSelects(root, option) {
    if (!option || !hasVariantSlotPanel(option)) return [];
    var panel = getSlotPanel(root, option.dataset.optionIndex);
    if (!panel) return [];
    return qsa('.pdp-purchase-options__slot-select', panel);
  }

  function getSlotSelects(root, option) {
    if (!isMixedPackOption(option)) return [];
    return getOptionSlotSelects(root, option);
  }

  function getSingleVariantSelection(root, option) {
    if (!isSingleVariantOption(option)) return null;

    var select = getOptionSlotSelects(root, option)[0];
    if (!select || !select.value) return null;

    var variantMap = getVariantMap(root);
    var variant = variantMap[select.value];
    return {
      variantId: select.value,
      available: variant ? !!variant.available : false,
    };
  }

  function isSingleVariantReady(root, option) {
    var selection = getSingleVariantSelection(root, option);
    return !!(selection && selection.variantId && selection.available);
  }

  function getOptionLabelFromSelect(select) {
    if (!select || !select.options.length) return '';
    var option = select.options[select.selectedIndex];
    if (!option) return '';
    return option.textContent.replace(/\s*[—–-]\s*Sold out$/i, '').trim();
  }

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var match = formatString.match(placeholderRegex);
    if (!match) return String(Number(cents) / 100);

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';
      if (isNaN(number) || number == null) return '0';
      number = (number / 100.0).toFixed(precision);
      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';
      return dollarsAmount + centsAmount;
    }

    var value = '';
    switch (match[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  }

  function formatProductSaleBadge(priceCents, compareCents) {
    compareCents = Math.max(0, parseInt(compareCents, 10) || 0);
    priceCents = Math.max(0, parseInt(priceCents, 10) || 0);
    if (!compareCents || compareCents <= priceCents) return '';
    var pct = Math.round(((compareCents - priceCents) / compareCents) * 100);
    if (pct > 0) return pct + '% Off';
    return 'On Sale';
  }

  function formatBundleExtraBadge(bundleDiscountPercent) {
    var pct = parseFloat(bundleDiscountPercent);
    if (Number.isNaN(pct) || pct <= 0) return '';
    var pctLabel = pct % 1 === 0 ? String(Math.round(pct)) : String(pct);
    return 'Extra ' + pctLabel + '% Off';
  }

  function buildPricingFromCents(priceCents, compareCents, root) {
    var format = root.dataset.moneyFormat || '${{amount}}';
    priceCents = Math.max(0, parseInt(priceCents, 10) || 0);
    compareCents = Math.max(0, parseInt(compareCents, 10) || 0);
    var onSale = compareCents > priceCents;
    var priceFormatted = formatMoney(priceCents, format);
    var compareFormatted = onSale ? formatMoney(compareCents, format) : '';
    var savingsFormatted = onSale ? formatProductSaleBadge(priceCents, compareCents) : '';

    return {
      priceFormatted: priceFormatted,
      compareFormatted: compareFormatted,
      savingsFormatted: savingsFormatted,
      price: priceCents,
      compare: compareCents,
    };
  }

  function parseDiscountPercentFromLabel(label) {
    if (!label) return 0;
    var match = String(label).match(/(\d+(?:\.\d+)?)\s*%/);
    return match ? parseFloat(match[1]) : 0;
  }

  function getBundleDiscountPercent(option) {
    if (!option) return 0;
    var pct = parseFloat(option.dataset.bundleDiscountPercent);
    if (!Number.isNaN(pct) && pct > 0) return Math.min(100, pct);
    return parseDiscountPercentFromLabel(option.dataset.savingsFormatted);
  }

  function applyBundleDiscount(subtotalCents, discountPercent) {
    if (!discountPercent || discountPercent <= 0) return subtotalCents;
    var discountCents = Math.round(subtotalCents * discountPercent / 100);
    return Math.max(0, subtotalCents - discountCents);
  }

  function buildBundlePricingFromSubtotal(subtotalCents, bundleDiscountPercent, root, variantIds) {
    var format = root.dataset.moneyFormat || '${{amount}}';
    subtotalCents = Math.max(0, parseInt(subtotalCents, 10) || 0);
    var priceCents = applyBundleDiscount(subtotalCents, bundleDiscountPercent);
    var savingsCents = subtotalCents - priceCents;
    var onSale = savingsCents > 0;
    var savingsFormatted =
      bundleDiscountPercent > 0 ? formatBundleExtraBadge(bundleDiscountPercent) : '';

    return {
      priceFormatted: formatMoney(priceCents, format),
      compareFormatted: onSale ? formatMoney(subtotalCents, format) : '',
      savingsFormatted: savingsFormatted,
      price: priceCents,
      compare: onSale ? subtotalCents : priceCents,
    };
  }

  function sumVariantListPrice(root, variantIds) {
    if (!variantIds || !variantIds.length) return 0;

    var map = getVariantMap(root);
    var subtotalCents = 0;

    variantIds.forEach(function (variantId) {
      var variant = map[String(variantId)];
      if (!variant) return;
      subtotalCents += variant.price || 0;
    });

    return subtotalCents;
  }

  function getPricingFromVariantIds(root, variantIds, option) {
    if (!variantIds || !variantIds.length) return null;

    var map = getVariantMap(root);
    var bundleDiscountPercent = getBundleDiscountPercent(option);
    var packQuantity = option ? parseInt(option.dataset.quantity, 10) || variantIds.length : variantIds.length;
    var isBundlePack = packQuantity > 1 || variantIds.length > 1;

    if (isBundlePack && bundleDiscountPercent > 0) {
      var subtotalCents = sumVariantListPrice(root, variantIds);
      if (!subtotalCents) return null;
      return buildBundlePricingFromSubtotal(subtotalCents, bundleDiscountPercent, root, variantIds);
    }

    var priceCents = 0;
    var compareCents = 0;

    variantIds.forEach(function (variantId) {
      var variant = map[String(variantId)];
      if (!variant) return;
      priceCents += variant.price || 0;
      var compare = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
      compareCents += compare || 0;
    });

    if (!priceCents) return null;
    return buildPricingFromCents(priceCents, compareCents > priceCents ? compareCents : 0, root);
  }

  function getPricingFromOptionButton(root, option) {
    if (!option) return null;

    var priceCents = parseInt(option.dataset.priceCents, 10);
    var compareCents = parseInt(option.dataset.compareCents, 10) || 0;
    var quantity = parseInt(option.dataset.quantity, 10) || 1;
    var hasLinkedVariant = !!option.dataset.variantId;
    var bundleDiscountPercent = getBundleDiscountPercent(option);

    if (!Number.isNaN(priceCents) && priceCents > 0) {
      if (hasLinkedVariant && quantity > 1 && !isMixedPackOption(option) && !isSingleVariantOption(option)) {
        var subtotalCents = priceCents * quantity;
        if (bundleDiscountPercent > 0) {
          var packVariantIds = [];
          for (var packIndex = 0; packIndex < quantity; packIndex++) {
            packVariantIds.push(option.dataset.variantId);
          }
          return buildBundlePricingFromSubtotal(
            subtotalCents,
            bundleDiscountPercent,
            root,
            packVariantIds
          );
        }
        priceCents = subtotalCents;
        if (compareCents > 0) compareCents *= quantity;
      }
      return buildPricingFromCents(priceCents, compareCents, root);
    }

    if (option.dataset.priceFormatted) {
      return {
        priceFormatted: option.dataset.priceFormatted,
        compareFormatted: option.dataset.compareFormatted || '',
        savingsFormatted: option.dataset.savingsFormatted || '',
        price: Number.isNaN(priceCents) ? 0 : priceCents,
        compare: compareCents,
      };
    }

    return null;
  }

  function resolveOptionPricing(root, option) {
    if (!root || !option) return null;

    if (isSingleVariantOption(option)) {
      var singleSelection = getSingleVariantSelection(root, option);
      if (!singleSelection || !singleSelection.variantId) return null;
      return getPricingFromVariantIds(root, [singleSelection.variantId], option);
    }

    if (isMixedPackOption(option)) {
      var selections = getSlotSelections(root, option);
      if (!selections.length) return null;
      return getPricingFromVariantIds(
        root,
        selections.map(function (selection) {
          return selection.variantId;
        }),
        option
      );
    }

    return getPricingFromOptionButton(root, option);
  }

  function updateHeroPriceDisplay(root, pricing) {
    if (!pricing || !pricing.priceFormatted) return;

    var hero = root.closest('[data-pdp-hero], .pdp-hero') || qs('[data-pdp-hero], .pdp-hero');
    if (!hero) return;

    var priceEl = qs('[data-pdp-price]', hero);
    var savingsEl = qs('[data-pdp-savings]', hero);
    if (!priceEl) return;

    var onSale = pricing.compare > pricing.price;
    if (onSale && pricing.compareFormatted) {
      priceEl.innerHTML =
        pricing.priceFormatted + ' <s data-pdp-compare>' + pricing.compareFormatted + '</s>';
    } else {
      priceEl.textContent = pricing.priceFormatted;
    }

    if (savingsEl) {
      if (pricing.savingsFormatted) {
        savingsEl.hidden = false;
        savingsEl.textContent = pricing.savingsFormatted;
      } else {
        savingsEl.hidden = true;
        savingsEl.textContent = '';
      }
    }

    var stickyPrice = qs('[data-pdp-sticky-price]');
    if (stickyPrice) stickyPrice.innerHTML = priceEl.innerHTML;
  }

  function syncHeroPricing(root, option) {
    updateHeroPriceDisplay(root, resolveOptionPricing(root, option));
  }

  function syncGalleryForVariant(root, variantId) {
    if (!variantId || !window.PdpHeroGallery || typeof window.PdpHeroGallery.goToVariantMedia !== 'function') {
      return;
    }

    var variant = getVariantMap(root)[String(variantId)];
    if (!variant || !variant.featured_media_id) return;

    var hero = root.closest('[data-pdp-hero], .pdp-hero') || qs('[data-pdp-hero], .pdp-hero');
    if (!hero) return;

    window.PdpHeroGallery.goToVariantMedia(hero, variant.featured_media_id);
  }

  function refreshVariantDisplay(root, option, syncGalleryVariantId) {
    if (!root || !option) return;

    if (syncGalleryVariantId) {
      syncGalleryForVariant(root, syncGalleryVariantId);
      return;
    }

    if (isSingleVariantOption(option)) {
      var singleSelection = getSingleVariantSelection(root, option);
      if (singleSelection && singleSelection.variantId) {
        syncGalleryForVariant(root, singleSelection.variantId);
      }
      return;
    }

    if (isMixedPackOption(option)) {
      var selections = getSlotSelections(root, option);
      if (selections.length && selections[0].variantId) {
        syncGalleryForVariant(root, selections[0].variantId);
      }
    }
  }

  function getAddedItemsPreview(root) {
    root = getRoot(root);
    if (!root) return [];

    var selected = getSelectedOption(root);
    if (!selected) return [];

    var variantMap = getVariantMap(root);

    if (isMixedPackOption(selected)) {
      return getSlotSelections(root, selected)
        .filter(function (selection) {
          return selection.variantId && selection.available;
        })
        .map(function (selection) {
          var variant = variantMap[selection.variantId];
          return {
            variantId: selection.variantId,
            title: variant ? variant.title : getOptionLabelFromSelect(
              getOptionSlotSelects(root, selected).find(function (select) {
                return select.value === String(selection.variantId);
              })
            ),
            quantity: 1,
          };
        });
    }

    if (isSingleVariantOption(selected)) {
      var singleSelection = getSingleVariantSelection(root, selected);
      if (!singleSelection || !singleSelection.variantId) return [];
      var singleVariant = variantMap[singleSelection.variantId];
      var singleSelect = getOptionSlotSelects(root, selected)[0];
      return [
        {
          variantId: singleSelection.variantId,
          title: singleVariant ? singleVariant.title : getOptionLabelFromSelect(singleSelect),
          quantity: 1,
        },
      ];
    }

    var linkedVariantId = selected.dataset.variantId;
    var linkedQty = parseInt(selected.dataset.quantity, 10) || 1;
    if (linkedVariantId) {
      var linkedVariant = variantMap[linkedVariantId];
      return [
        {
          variantId: linkedVariantId,
          title: linkedVariant ? linkedVariant.title : '',
          quantity: linkedQty,
        },
      ];
    }

    return [];
  }

  function getSlotSelections(root, option) {
    if (!option || !isMixedPackOption(option)) return [];

    var variantMap = getVariantMap(root);
    return getSlotSelects(root, option).map(function (select) {
      if (!select) return null;
      var variantId = select.value;
      if (!variantId) return null;
      var variant = variantMap[variantId];
      return {
        slotIndex: parseInt(select.dataset.slotIndex, 10),
        variantId: variantId,
        available: variant ? !!variant.available : false,
      };
    }).filter(function (selection) {
      return selection != null;
    });
  }

  function areSlotsReady(root, option) {
    if (!isMixedPackOption(option)) return true;

    var slotCount = parseInt(option.dataset.slotCount, 10);
    var selections = getSlotSelections(root, option);
    if (Number.isNaN(slotCount) || slotCount < 1) return false;
    if (selections.length !== slotCount) return false;

    return selections.every(function (selection) {
      return selection.variantId && selection.available;
    });
  }

  function isMixedModeActive(root) {
    root = getRoot(root);
    if (!root) return false;
    return isMixedPackOption(getSelectedOption(root));
  }

  function applyVariant(form, variantId) {
    if (!form || !variantId) return false;

    var select = qs('select[name="id"]', form);
    if (select) {
      var option = qs('option[value="' + variantId + '"]', select);
      if (!option || option.disabled) return false;
      if (select.value === String(variantId)) return true;
      select.value = String(variantId);
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return true;
    }

    var pill = qs(
      '[data-variant-pill][data-variant-id="' + variantId + '"]',
      form.closest('[data-pdp-hero], .pdp-hero') || document
    );
    if (pill && !pill.disabled) {
      pill.click();
      return true;
    }

    return false;
  }

  function applyQuantity(form, quantity) {
    if (!form || !quantity) return;
    var qty = parseInt(quantity, 10);
    if (Number.isNaN(qty) || qty < 1) return;
    var input = qs('[data-qty-input], input[name="quantity"]', form);
    if (!input) return;
    input.value = String(qty);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  function setReady(root, ready) {
    root.dataset.purchaseOptionsReady = ready ? 'true' : 'false';
  }

  function syncSlotPanels(root, selectedOption) {
    qsa('[data-purchase-option-slots]', root).forEach(function (panel) {
      var show =
        selectedOption &&
        hasVariantSlotPanel(selectedOption) &&
        panel.dataset.optionIndex === selectedOption.dataset.optionIndex;
      panel.hidden = !show;
    });
  }

  function syncDynamicCheckout(root) {
    var wrap = qs('[data-pdp-dynamic-checkout]');
    if (!wrap) return;
    var hide = isMixedModeActive(root);
    wrap.hidden = hide;
    wrap.setAttribute('aria-hidden', hide ? 'true' : 'false');
  }

  function refreshLegacyPurchaseState(form) {
    if (!form) return;
    var select = qs('select[name="id"]', form);
    if (select) {
      select.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    var checkedPill = qs('[data-variant-pill][aria-checked="true"], [data-variant-pill].is-selected', form.closest('[data-pdp-hero], .pdp-hero') || document);
    if (checkedPill) checkedPill.click();
  }

  function syncPurchaseCta(root, form) {
    if (!form) return;

    var submitBtn = qs('[type="submit"]', form);
    var stickyBtn = qs('[data-pdp-sticky-submit]');
    var mixed = isMixedModeActive(root);
    var selected = getSelectedOption(root);
    var single = isSingleVariantOption(selected);

    if (!mixed) {
      [submitBtn, stickyBtn].forEach(function (btn) {
        if (!btn || btn.dataset.pdpMixedGated !== 'true') return;
        delete btn.dataset.pdpMixedGated;
        if (!btn.disabled) btn.removeAttribute('aria-disabled');
      });

      if (single) {
        var singleReady = isSingleVariantReady(root, selected);
        [submitBtn, stickyBtn].forEach(function (btn) {
          if (!btn) return;
          btn.disabled = !singleReady;
          btn.setAttribute('aria-disabled', singleReady ? 'false' : 'true');
        });
        return;
      }

      refreshLegacyPurchaseState(form);
      return;
    }

    var ready = areSlotsReady(root, selected);
    var blockSubmit = !ready;

    [submitBtn, stickyBtn].forEach(function (btn) {
      if (!btn) return;
      btn.disabled = blockSubmit;
      btn.setAttribute('aria-disabled', blockSubmit ? 'true' : 'false');
      btn.dataset.pdpMixedGated = blockSubmit ? 'true' : 'false';
    });
  }

  function evaluateReady(root, option, form, variantSynced) {
    if (isMixedPackOption(option)) return areSlotsReady(root, option);
    if (isSingleVariantOption(option)) return isSingleVariantReady(root, option);
    var variantId = option.dataset.variantId;
    if (!variantId) return true;
    return variantSynced !== false;
  }

  function syncSingleVariantToForm(root, option, form) {
    if (!isSingleVariantOption(option)) return false;

    applyQuantity(form, 1);
    var selection = getSingleVariantSelection(root, option);
    if (!selection || !selection.variantId) return false;
    return applyVariant(form, selection.variantId);
  }

  function applyOptionState(root, option, form) {
    if (!option) return;

    qsa('[data-purchase-option]', root).forEach(function (el) {
      var selected = el === option;
      el.classList.toggle('is-selected', selected);
      el.setAttribute('aria-checked', selected ? 'true' : 'false');
    });

    var variantId = option.dataset.variantId;
    var quantity = option.dataset.quantity;
    var variantOk = true;
    var mixed = isMixedPackOption(option);
    var single = isSingleVariantOption(option);

    syncSlotPanels(root, option);

    if (single) {
      variantOk = syncSingleVariantToForm(root, option, form);
    } else if (!mixed) {
      if (variantId) variantOk = applyVariant(form, variantId);
      if (quantity) applyQuantity(form, quantity);
    } else {
      applyQuantity(form, 1);
    }

    var ready = evaluateReady(root, option, form, variantOk);
    setReady(root, ready);
    syncDynamicCheckout(root);
    syncPurchaseCta(root, form);
    refreshVariantDisplay(root, option);
    syncHeroPricing(root, option);

    var singleSelection = single ? getSingleVariantSelection(root, option) : null;
    var resolvedVariantId = mixed
      ? null
      : single
        ? singleSelection && singleSelection.variantId
          ? singleSelection.variantId
          : null
        : variantId || null;

    root.dispatchEvent(
      new CustomEvent('pdp:purchase-option-change', {
        bubbles: true,
        detail: {
          optionIndex: option.dataset.optionIndex || null,
          selectionMode: option.dataset.selectionMode || 'same_variant',
          mixedMode: mixed,
          singleVariantMode: single,
          variantId: resolvedVariantId,
          quantity: mixed ? null : single ? '1' : quantity || null,
          variantSynced: mixed ? null : variantOk,
          slotSelections: mixed ? getSlotSelections(root, option) : null,
          ready: ready,
        },
      })
    );
  }

  function selectOption(root, option, form) {
    if (!option) return;
    applyOptionState(root, option, form);
  }

  function bindMixedSubmitGuard(root, form, signal) {
    if (!form || form.dataset.pdpMixedSubmitGuard === 'true') return;
    form.dataset.pdpMixedSubmitGuard = 'true';

    form.addEventListener(
      'submit',
      function (event) {
        if (!isMixedModeActive(root)) return;
        if (areSlotsReady(root, getSelectedOption(root))) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        if (typeof window.PdpPurchaseOptions.scrollIntoView === 'function') {
          window.PdpPurchaseOptions.scrollIntoView();
        }
      },
      { capture: true, signal: signal }
    );
  }

  function initPurchaseOptions(root) {
    if (!root || root.dataset.pdpPurchaseOptionsInit === 'true') return;
    root.dataset.pdpPurchaseOptionsInit = 'true';

    var form = getHeroForm(root);
    var options = qsa('[data-purchase-option]', root);
    if (!options.length) return;

    var ac = new AbortController();
    var signal = ac.signal;

    options.forEach(function (option) {
      option.addEventListener(
        'click',
        function () {
          selectOption(root, option, form);
        },
        { signal: signal }
      );

      option.addEventListener(
        'keydown',
        function (event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          selectOption(root, option, form);
        },
        { signal: signal }
      );
    });

    root.addEventListener(
      'change',
      function (event) {
        if (!event.target.matches('.pdp-purchase-options__slot-select')) return;
        var selected = getSelectedOption(root);
        if (!selected) return;

        if (isSingleVariantOption(selected)) {
          var variantOk = syncSingleVariantToForm(root, selected, form);
          var singleReady = evaluateReady(root, selected, form, variantOk);
          setReady(root, singleReady);
          syncPurchaseCta(root, form);
          refreshVariantDisplay(root, selected, event.target.value);
          syncHeroPricing(root, selected);

          var singleSelection = getSingleVariantSelection(root, selected);

          root.dispatchEvent(
            new CustomEvent('pdp:purchase-option-slots-change', {
              bubbles: true,
              detail: {
                optionIndex: selected.dataset.optionIndex || null,
                singleVariantMode: true,
                variantId: singleSelection ? singleSelection.variantId : null,
                ready: singleReady,
              },
            })
          );
          return;
        }

        if (!isMixedPackOption(selected)) return;

        var ready = areSlotsReady(root, selected);
        setReady(root, ready);
        syncPurchaseCta(root, form);
        refreshVariantDisplay(root, selected, event.target.value);
        syncHeroPricing(root, selected);

        root.dispatchEvent(
          new CustomEvent('pdp:purchase-option-slots-change', {
            bubbles: true,
            detail: {
              optionIndex: selected.dataset.optionIndex || null,
              slotSelections: getSlotSelections(root, selected),
              ready: ready,
            },
          })
        );
      },
      { signal: signal }
    );

    bindMixedSubmitGuard(root, form, signal);

    var selected = getSelectedOption(root) || options[0];
    applyOptionState(root, selected, form);

    root._pdpPurchaseOptionsCleanup = function () {
      ac.abort();
      delete root.dataset.pdpPurchaseOptionsInit;
      if (form) delete form.dataset.pdpMixedSubmitGuard;
    };
  }

  function initScope(scope) {
    qsa('[data-pdp-purchase-options]', scope || document).forEach(initPurchaseOptions);
  }

  function getCartItems(root) {
    root = getRoot(root);
    if (!root || !isMixedModeActive(root) || !areSlotsReady(root, getSelectedOption(root))) return null;

    return getSlotSelections(root, getSelectedOption(root))
      .filter(function (selection) {
        var id = parseInt(selection.variantId, 10);
        return selection.available && !Number.isNaN(id) && id > 0;
      })
      .map(function (selection) {
        return {
          id: parseInt(selection.variantId, 10),
          quantity: 1,
        };
      });
  }

  window.PdpPurchaseOptions = {
    init: initScope,
    scrollIntoView: function () {
      var root = qs('[data-pdp-purchase-options]');
      if (!root) return;
      root.scrollIntoView({ behavior: 'smooth', block: 'center' });
      var selected = qs('[data-purchase-option].is-selected', root) || qs('[data-purchase-option]', root);
      if (selected) selected.focus({ preventScroll: true });
    },
    isReady: function (root) {
      root = getRoot(root);
      if (!root) return true;
      return root.dataset.purchaseOptionsReady !== 'false';
    },
    isMixedModeActive: function (root) {
      return isMixedModeActive(root);
    },
    getSlotSelections: function (root) {
      root = getRoot(root);
      if (!root) return [];
      return getSlotSelections(root, getSelectedOption(root));
    },
    getAddedItemsPreview: getAddedItemsPreview,
    getCartItems: getCartItems,
  };

  if (document.readyState !== 'loading') initScope(document);
  else document.addEventListener('DOMContentLoaded', function () { initScope(document); }, { once: true });

  document.addEventListener('shopify:section:load', function (event) {
    initScope(event.target);
  });
})();
